import { App } from 'antd';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { QuestionWrapper } from 'common/components';
import { ClientForm } from 'common/forms';
import { toPersistedClientFormValues } from 'common/forms/persistClientFormValues';
import {
  getAddressValidationFingerprint,
  hasCompletedAddressValidationForFingerprint,
  isAddressValidationCompletedStatus,
} from 'common/addressValidation/addressValidationFingerprint';
import {
  applyClientPostalCodeValidationErrors,
  shouldBlockContinueWithoutPostalCode,
  showClientAddressFormValidationErrors,
} from 'common/addressValidation/clientAddressFormValidation';
import { withAddressValidationMeta } from 'common/addressValidation/applyAddressValidation';
import { MessageApi, ModalApi, runClientAddressValidation } from 'common/addressValidation/runClientAddressValidation';
import { ClientFormValues } from '../../../../../../common/models/Forms';
import { I18nNameSpaces } from '../../../../../../common/services/i18n';

import { QuestionTitle, PageControls } from '../../../../components';
import ClientIdentityDocumentUpload from '../../../../components/ClientIdentityDocumentUpload';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectInformationsCustomerInfo1,
  selectIsCustomerInfo2Visible,
  setInformationsClient1InfoValuesAction,
} from '../../../../ducks';
import { useFormStepContinueRef } from '../../../../hooks/useFormStepContinueRef';
import { FeatureProps } from '../../../../index';

async function resolveStepAddressValues({
  values,
  formikRef,
  tForms,
  message,
  modal,
  addressFingerprint,
  lastValidatedFingerprint,
  lastFailedFingerprint,
}: {
  values: ClientFormValues;
  formikRef: { current: FormikProps<ClientFormValues> | null };
  tForms: TFunction;
  message: MessageApi;
  modal: ModalApi;
  addressFingerprint: string;
  lastValidatedFingerprint: string | undefined;
  lastFailedFingerprint: string | undefined;
}): Promise<{ outcome: 'invalid' | 'ready'; values: ClientFormValues }> {
  if (hasCompletedAddressValidationForFingerprint(values, addressFingerprint, lastValidatedFingerprint)) {
    return { outcome: 'ready', values };
  }

  if (lastFailedFingerprint === addressFingerprint) {
    return { outcome: 'ready', values: withAddressValidationMeta(values, 'user_override') };
  }

  const validationOutcome = await runClientAddressValidation(values, {
    formikRef,
    tForms,
    message,
    modal,
  });

  if (validationOutcome.type === 'invalid') {
    return { outcome: 'invalid', values };
  }

  return { outcome: 'ready', values: validationOutcome.values };
}

export default function CustomerInfo1Feature({ isPrivileged }: FeatureProps) {
  const { t } = useTranslation(I18nNameSpaces.Common);
  const { t: tForms } = useTranslation(I18nNameSpaces.Forms);
  const { message, modal } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialValues = useSelector(selectInformationsCustomerInfo1);
  const isCustomerInfo2Visible = useSelector(selectIsCustomerInfo2Visible);
  const { bindFormik, continueWithSoftValidation, showSoftContinueHint, formikRef } =
    useFormStepContinueRef<ClientFormValues>();
  const [isAddressValidationPending, setIsAddressValidationPending] = useState(false);
  const lastValidatedAddressFingerprintRef = useRef<string | undefined>(
    isAddressValidationCompletedStatus(initialValues?.addressValidationStatus)
      ? getAddressValidationFingerprint(initialValues)
      : undefined,
  );
  const lastFailedAddressValidationFingerprintRef = useRef<string | undefined>(undefined);

  function handleBackClick() {
    navigate(-1);
  }

  async function handleContinueClick() {
    if (isAddressValidationPending) {
      return;
    }

    const formik = formikRef.current;
    if (!formik) {
      return;
    }

    const hasAddressFormErrors = await showClientAddressFormValidationErrors(formik);
    if (hasAddressFormErrors) {
      await continueWithSoftValidation(() => undefined);
      return;
    }

    const addressFingerprint = getAddressValidationFingerprint(formik.values);
    setIsAddressValidationPending(true);

    let resolvedAddress: { outcome: 'invalid' | 'ready'; values: ClientFormValues };
    try {
      resolvedAddress = await resolveStepAddressValues({
        values: formik.values,
        formikRef,
        tForms,
        message,
        modal,
        addressFingerprint,
        lastValidatedFingerprint: lastValidatedAddressFingerprintRef.current,
        lastFailedFingerprint: lastFailedAddressValidationFingerprintRef.current,
      });
    } finally {
      setIsAddressValidationPending(false);
    }

    if (resolvedAddress.outcome === 'invalid') {
      lastFailedAddressValidationFingerprintRef.current = addressFingerprint;
      await continueWithSoftValidation(() => undefined);
      return;
    }

    const valuesAfterAddressValidation = resolvedAddress.values;
    if (
      getAddressValidationFingerprint(formik.values) !== getAddressValidationFingerprint(valuesAfterAddressValidation)
    ) {
      await formik.setValues(valuesAfterAddressValidation);
    }

    if (valuesAfterAddressValidation.addressValidationStatus) {
      lastValidatedAddressFingerprintRef.current = getAddressValidationFingerprint(valuesAfterAddressValidation);
    }

    const validatedAddressSnapshot = valuesAfterAddressValidation;

    await continueWithSoftValidation((values) => {
      if (shouldBlockContinueWithoutPostalCode(values)) {
        formik.setFieldError('psc', tForms('form.required'));
        void formik.setFieldTouched('psc', true, false);
        return;
      }

      if (applyClientPostalCodeValidationErrors(formik, values, tForms)) {
        return;
      }

      handleSubmit(values);
    }, validatedAddressSnapshot);
  }

  function handleSubmit(values: ClientFormValues) {
    const persistedValues = toPersistedClientFormValues(values);
    if (!isPrivileged) {
      dispatch(setInformationsClient1InfoValuesAction(persistedValues));
    }
    if (isCustomerInfo2Visible) {
      navigate(`/form/${id}/informations/customer-info-2`);
    } else {
      navigate(`/form/${id}/investing/experience`);
    }
  }

  return (
    <>
      <QuestionTitle>{t('informationsFeature.clientOne')}</QuestionTitle>
      {id ? <ClientIdentityDocumentUpload transactionId={id} clientIndex={1} disabled={isPrivileged} /> : null}
      <QuestionWrapper>
        <ClientForm
          bindSubmit={bindFormik}
          initialValues={initialValues || undefined}
          onSubmit={() => {
            void handleContinueClick();
          }}
          withIdentityDocumentRegistryCheck
        />
      </QuestionWrapper>
      <PageControls
        softContinueHint={showSoftContinueHint}
        disableContinue={isAddressValidationPending}
        onBack={handleBackClick}
        onContinue={() => {
          void handleContinueClick();
        }}
      />
    </>
  );
}
